import { forwardRef, type ButtonHTMLAttributes, type MouseEvent, type ReactNode } from 'react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import './Button.css';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Render the child element (for example a router Link) with the button's styling. */
  asChild?: boolean;
  loading?: boolean;
  leadingIcon?: ReactNode;
}

/**
 * A loading button is doing the thing already, so a second click or Enter cannot send it again. It is
 * `aria-disabled`, not `disabled`: a disabled button drops the keyboard focus to the page, which threw
 * a person out of the sheet they were in every time they pressed its button (pass 3, 2026-10-06). So
 * it keeps the focus, says it is busy, and swallows the press (a submit button's as well, and with it
 * Enter in the form's fields). `data-off` marks one the caller turned off, which is really disabled
 * and drawn dimmed.
 */
function busyProps(loading?: boolean, disabled?: boolean) {
  return {
    'data-loading': loading ? '' : undefined,
    'aria-busy': loading || undefined,
    'aria-disabled': loading && !disabled ? true : undefined,
    'data-off': disabled ? '' : undefined,
    disabled: disabled || undefined,
  };
}

/** The press of a button that is already doing it: nothing happens, and no form is sent. */
function swallow(event: MouseEvent<HTMLButtonElement>): void {
  event.preventDefault();
  event.stopPropagation();
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    asChild,
    loading,
    leadingIcon,
    className,
    children,
    ...rest
  },
  ref,
) {
  const Comp = asChild ? Slot : 'button';
  const classes = ['pui-button', `pui-button--${variant}`, `pui-button--${size}`, className]
    .filter(Boolean)
    .join(' ');
  return (
    <Comp
      ref={ref}
      className={classes}
      type={asChild ? undefined : (rest.type ?? 'button')}
      {...rest}
      {...busyProps(loading, rest.disabled)}
      onClick={loading ? swallow : rest.onClick}
    >
      {leadingIcon ? <span className="pui-button__icon">{leadingIcon}</span> : null}
      <Slottable>{children}</Slottable>
    </Comp>
  );
});
